"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, CircleAlert as AlertCircle, FileCheck, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const CHECKLIST_ITEMS = [{
  index: "01",
  label: "The exact machine being purchased"
}, {
  index: "02",
  label: "The equipment included with the order"
}, {
  index: "03",
  label: "Product quantity"
}, {
  index: "04",
  label: "Delivery destination"
}, {
  index: "05",
  label: "Destination contact information"
}, {
  index: "06",
  label: "Access or unloading considerations"
}, {
  index: "07",
  label: "Available packed dimensions and weight"
}, {
  index: "08",
  label: "Delivery arrangement"
}, {
  index: "09",
  label: "Any order-specific documentation"
}, {
  index: "10",
  label: "Contact information for questions about the shipment"
}];
export default function Checklist() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.04
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="predispatchchecklist" data-nav="light" className="w-full bg-background py-16 md:py-24 border-b border-border">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-block w-2 h-2 bg-primary rounded-none" />
            <p className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
              Dispatch Verification
            </p>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground leading-[1.05]">
            What to Confirm Before Your Mechanical Bull Ships
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Review and lock each critical requirement before freight pickup. Proper carrier coordination ensures safe handling, correct access equipment, and no transit holds.
          </p>
        </div>

        {/* Checklist Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {CHECKLIST_ITEMS.map((item, i) => <motion.div key={item.index} data-index={i} variants={itemVariants} className="group relative flex items-start gap-4 p-4 sm:p-5 bg-card border border-border transition-colors duration-180 hover:border-foreground">
              <div className="mt-0.5 shrink-0 w-7 h-7 flex items-center justify-center bg-muted border border-border text-primary group-hover:border-primary transition-colors">
                <Check className="size-4 stroke-[2.5]" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono text-xs text-muted-foreground tracking-wider">
                    REQ-{item.index}
                  </span>
                  <span className="font-mono text-[10px] uppercase text-muted-foreground/70 tracking-widest border border-border/50 px-1.5 py-0.5 bg-muted/40">
                    Mandatory
                  </span>
                </div>
                <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground tracking-normal uppercase leading-snug">
                  {item.label}
                </h3>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Closing Advisory Plate */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.1
      }} className="mt-10 sm:mt-12 p-6 sm:p-8 bg-muted border-2 border-border relative overflow-hidden">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-background border border-border shrink-0 mt-0.5">
                <AlertCircle className="size-6 text-primary stroke-[2]" />
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1.5 font-medium">
                  Dispatch Advisory Notice
                </p>
                <p className="font-serif text-lg sm:text-xl font-bold uppercase tracking-tight text-foreground leading-snug max-w-3xl">
                  Confirm order-specific information before dispatch instead of relying on assumptions from another machine or previous shipment.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row w-full sm:w-auto items-stretch sm:items-center gap-3 shrink-0">
              <Button asChild variant="outline" className="rounded-none border-border font-mono text-xs uppercase tracking-wider">
                <Link to="/ShippingWarrantyAndOrderingPage">
                  <FileCheck className="mr-1.5 size-4" />
                  Freight Specs
                </Link>
              </Button>
              <Button asChild className="rounded-none font-mono text-xs uppercase tracking-wider">
                <Link to="/ContactPage">
                  <Phone className="mr-1.5 size-4" />
                  Verify Order
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}